import socketHelper from '@chronos/utils/socketHelper';
import { chronosBaseApi } from './chronosBaseApi';

const sortCompare = (a, b) => {
  if (a.id < b.id) {
    return -1;
  }
  if (a.id > b.id) {
    return 1;
  }
  return 0;
};

const apiName = 'Estimate';
const providesList = (result = []) => {
  return result.length
    ? [
        { type: apiName, id: 'LIST' },
        ...result.map(({ id }) => ({ type: apiName, id })),
      ]
    : [{ type: apiName, id: 'LIST' }];
};

const apiWithTag = chronosBaseApi.enhanceEndpoints({
  addTagTypes: [apiName],
});
const estimatesApi = apiWithTag.injectEndpoints({
  endpoints: (build) => ({
    getEstimates: build.query({
      query: ({ cardIds }) => ({
        url: `/trello/estimates`,
        method: 'GET',
        params: {
          cardIds,
        },
      }),
      transformResponse(response, meta, arg) {
        return response.sort(sortCompare);
      },
      providesTags: (result) => providesList(result),
      async onCacheEntryAdded(
        { cardIds },
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        if (!socketHelper.getSocket()) {
          // socketHelper is not available in the storybook environment
          return;
        }
        // create a websocket connection when the cache subscription starts
        const listener = (action) => {
          const { payload: estimate } = action;
          // skip if the estimate is not for the current card
          if (
            action.type === 'SET_ESTIMATE' &&
            !cardIds.includes(estimate.cardId)
          ) {
            return;
          }

          log.info('[socket:estimateState]:', action);
          updateCachedData((draft) => {
            const estimates = draft.filter((item) => item.id !== estimate.id);
            if (action.type === 'SET_ESTIMATE') {
              return estimates.concat(estimate).sort(sortCompare);
            }
            if (action.type === 'DELETE_ESTIMATE') {
              return estimates;
            }
            return estimates;
          });
        };

        try {
          // wait for the initial query to resolve before proceeding
          await cacheDataLoaded;
          socketHelper.on('estimateState', listener);
        } catch (e) {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
          throw e;
        }
        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved;
        // perform cleanup steps once the `cacheEntryRemoved` promise resolves
        socketHelper.off('estimateState', listener);
      },
    }),
    saveEstimate: build.mutation({
      invalidatesTags: (result, error, { id }) => [{ type: apiName, id }],
      query: ({ id, ...body }) => {
        if (id) {
          return {
            url: `/trello/estimates/${id}`,
            method: 'PUT',
            body,
          };
        }
        return {
          url: '/trello/estimates',
          method: 'POST',
          body,
        };
      },
    }),
    deleteEstimate: build.mutation({
      invalidatesTags: (result, error, { id }) => [{ type: apiName, id }],
      query: ({ id }) => ({
        url: `/trello/estimates/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetEstimatesQuery,
  useSaveEstimateMutation,
  useDeleteEstimateMutation,
} = estimatesApi;
export default estimatesApi;
